import {
  AdminApiError,
  type AdminClient,
  type Availability,
  type InfraProbe,
  type IntegratorProbe,
  type ServiceProbeResult,
  type SystemHealth,
} from '@apx/admin-sdk';
import { fmtTime } from '../format.js';
import { showToast } from '../toast.js';

export interface SystemHealthPageDeps {
  client: AdminClient;
}

const REFRESH_INTERVAL_MS = 30_000;

export async function renderSystemHealthPage(
  root: HTMLElement,
  deps: SystemHealthPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">System health</h1>
    <p class="page-sub">Live reachability + latency of Postgres, Redis, and every backend service, with rolling uptime % from a background sampler (every 30s) and each service's process run-time. Auto-refreshes every ${REFRESH_INTERVAL_MS / 1000}s.</p>
    <div id="sh-body"><p class="muted">Loading…</p></div>
  `;
  const bodyEl = root.querySelector<HTMLElement>('#sh-body')!;
  let timer: number | undefined;

  async function tick(): Promise<void> {
    try {
      const health = await deps.client.fetchSystemHealth();
      bodyEl.innerHTML = renderBody(health);
      bodyEl.querySelectorAll<HTMLButtonElement>('[data-copy-url]').forEach((btn) => {
        btn.addEventListener('click', () => {
          void navigator.clipboard?.writeText(btn.dataset.copyUrl ?? '').then(
            () => showToast('URL copied.', 'success'),
            () => showToast('Copy failed.', 'error'),
          );
        });
      });
    } catch (err) {
      bodyEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  /**
   * Internal service/integrator URLs use Docker DNS names (e.g. `gateway`) the
   * browser can't resolve. When the admin is on localhost the host publishes
   * those ports, so we rewrite the host to `localhost` and link it — that IS
   * reachable. Otherwise (prod, services not publicly exposed) the URL is shown
   * as a copyable chip only, since linking it would just NXDOMAIN.
   */
  function browserReachable(url: string): string | null {
    const onLocalhost = ['localhost', '127.0.0.1'].includes(location.hostname);
    if (!onLocalhost) return null;
    // Swap only the host segment, keep scheme/port/path: http://gateway:53000/x
    const rewritten = url.replace(/^(https?:\/\/)[^/:]+(:\d+)?/, '$1localhost$2');
    return rewritten === url && location.hostname !== 'localhost' ? null : rewritten;
  }

  function urlChip(url: string): string {
    const reachable = browserReachable(url);
    // Linkable: show + copy the localhost form (what actually opens for them).
    // Not linkable: show + copy the internal URL for host-shell probing.
    const shown = reachable ?? url;
    const label = reachable
      ? `<a href="${escapeHtml(reachable)}" target="_blank" rel="noopener">${escapeHtml(reachable)}</a>`
      : `<code>${escapeHtml(url)}</code>`;
    return `<span class="sh-url">${label}<button type="button" class="sh-url-copy" data-copy-url="${escapeHtml(shown)}" title="Copy URL">copy</button></span>`;
  }

  function renderBody(h: SystemHealth): string {
    const overallPill = h.overall === 'healthy'
      ? '<span class="pill live">healthy</span>'
      : '<span class="pill warn">degraded</span>';
    // Integrator endpoints are third-party; their status is shown on its own
    // section, not folded into the gamehub-owned "Overall" pill. Falls back to
    // deriving from the rows when an older backend omits `integratorsStatus`.
    const integratorsHealthy =
      h.integratorsStatus !== undefined
        ? h.integratorsStatus === 'healthy'
        : (h.integrators ?? [])
            .filter((i) => i.status !== 'skipped')
            .every((i) => i.status === 'up');
    const integratorsPill = integratorsHealthy
      ? '<span class="pill live">healthy</span>'
      : '<span class="pill warn">degraded</span>';
    return `
      <div class="card sh-overall ${h.overall === 'healthy' ? 'sh-overall--ok' : 'sh-overall--degraded'}">
        <div class="row" style="justify-content:space-between;margin-top:0">
          <h2 style="margin:0">Overall ${overallPill}</h2>
          <span class="muted">checked ${fmtTime(h.checkedAt)}</span>
        </div>
      </div>

      <div class="sh-grid">
        <div class="sh-col">
          <div class="card" style="margin-bottom:0">
            <h2>Infrastructure</h2>
            ${infraRow('Postgres', h.infra.postgres)}
            ${infraRow('Redis', h.infra.redis)}
          </div>
          <div class="card" style="margin-bottom:0">
            <div class="row" style="justify-content:space-between;margin-top:0">
              <h2 style="margin:0">Integrator wallet endpoints ${integratorsPill}</h2>
            </div>
            <p class="muted" style="font-size:12px;margin:8px 0 12px">
              HMAC-signed zero-data <code>/balance</code> ping to each active
              integrator <code>walletBaseUrl</code>. 4xx counts as "up" — endpoint
              responded and accepted the signature. Skipped integrators need their
              wallet config set on the Integrator detail page.
            </p>
            ${
              (h.integrators ?? []).length === 0
                ? '<p class="muted">No active integrators.</p>'
                : (h.integrators ?? []).map(integratorRow).join('')
            }
          </div>
        </div>
        <div class="card" style="margin-bottom:0">
          <h2>Backend services</h2>
          <p class="muted" style="font-size:12px;margin:0 0 10px">
            ${
              ['localhost', '127.0.0.1'].includes(location.hostname)
                ? 'Internal Docker names (e.g. <code>gateway</code>) — links point at the host-published <code>localhost</code> port so they open here.'
                : 'Internal service URLs (Docker DNS / private network) — reachable from the server, not your browser. Copy one to probe from a shell on the host.'
            }
          </p>
          ${h.services.map(serviceRow).join('')}
        </div>
      </div>
    `;
  }

  function infraRow(label: string, p: InfraProbe): string {
    const pill = p.status === 'up'
      ? '<span class="pill live">up</span>'
      : '<span class="pill warn">down</span>';
    return `<div class="sh-row${p.status !== 'up' ? ' sh-row--down' : ''}">
      <div>
        <div>${label}</div>
        ${availLine(p.availability)}
        ${p.error ? `<div class="error" style="font-size:11px;margin-top:2px">${escapeHtml(p.error)}</div>` : ''}
      </div>
      <div style="text-align:right">
        ${pill}
        <div style="font-size:11px;margin-top:2px">${latencyHtml(p.latencyMs)}</div>
      </div>
    </div>`;
  }

  /** "3d 5h", "2h 14m", "8m", "12s" — coarse, two-unit duration. */
  function fmtDuration(ms: number): string {
    const s = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m`;
    return `${s}s`;
  }

  /** Pick the widest window that has data, so a freshly-booted box still shows
   *  a number (1h) and a long-lived one shows the meaningful 24h/7d figure. */
  function pickAvail(a: Availability): { pct: number; window: string } | null {
    if (a.pct7d !== null && a.coverageMs >= 6 * 24 * 3600_000) return { pct: a.pct7d, window: '7d' };
    if (a.pct24h !== null && a.coverageMs >= 23 * 3600_000) return { pct: a.pct24h, window: '24h' };
    if (a.pct1h !== null) return { pct: a.pct1h, window: a.coverageMs >= 3600_000 ? '1h' : `${fmtDuration(a.coverageMs)}` };
    return null;
  }

  function availClass(pct: number): string {
    if (pct >= 99.9) return 'live';
    if (pct >= 99) return '';
    return 'warn';
  }

  /** Colour latency so a slow probe pops: fast stays calm, amber/red escalate. */
  function latencyHtml(ms: number): string {
    const cls = ms < 100 ? 'sh-lat' : ms < 300 ? 'sh-lat sh-lat--warn' : 'sh-lat sh-lat--slow';
    return `<span class="${cls}">${ms}ms</span>`;
  }

  function availLine(a: Availability | undefined): string {
    if (!a) return '';
    const picked = pickAvail(a);
    if (!picked) return '<div class="muted" style="font-size:11px;margin-top:3px">uptime: collecting…</div>';
    const pctStr = picked.pct === 100 ? '100%' : `${picked.pct.toFixed(1)}%`;
    return `<div style="font-size:11px;margin-top:3px"><span class="pill ${availClass(picked.pct)}" style="font-size:10px">${pctStr} ${picked.window}</span></div>`;
  }

  function serviceRow(s: ServiceProbeResult): string {
    const pill = s.status === 'up'
      ? '<span class="pill live">up</span>'
      : '<span class="pill warn">down</span>';
    const urlLine = s.url
      ? `<div style="margin-top:3px">${urlChip(s.url)}</div>`
      : s.self
        ? '<div class="muted" style="font-size:12px;margin-top:2px">this service · aggregator (always up if this page loads)</div>'
        : '';
    // admin-api is the aggregator, not probed — its 0ms isn't a measurement.
    const latency = s.self ? '<span class="muted">self</span>' : latencyHtml(s.latencyMs);
    const uptime =
      s.uptimeMs !== undefined
        ? `<div class="muted" style="font-size:11px;margin-top:3px">running ${fmtDuration(s.uptimeMs)}</div>`
        : '';
    return `<div class="sh-row${s.status !== 'up' && !s.self ? ' sh-row--down' : ''}">
      <div style="min-width:0">
        <div><code>${escapeHtml(s.name)}</code></div>
        ${urlLine}
        ${uptime}
        ${availLine(s.availability)}
        ${s.error ? `<div class="error" style="font-size:11px;margin-top:2px">${escapeHtml(s.error)}</div>` : ''}
      </div>
      <div style="text-align:right;margin-left:12px">
        ${pill}
        <div class="muted" style="font-size:11px;margin-top:2px">${latency}</div>
      </div>
    </div>`;
  }

  function integratorRow(i: IntegratorProbe): string {
    const pill =
      i.status === 'up'
        ? '<span class="pill live">up</span>'
        : i.status === 'skipped'
        ? '<span class="pill">skipped</span>'
        : '<span class="pill warn">down</span>';
    const httpBit = i.httpStatus ? `<span class="muted" style="font-size:11px">HTTP ${i.httpStatus}</span>` : '';
    return `<div style="display:flex;justify-content:space-between;align-items:baseline;padding:8px 0;border-bottom:1px solid var(--border)">
      <div style="min-width:0;flex:1">
        <div><a href="/integrators/${i.integratorId}" style="color:var(--accent-2);text-decoration:none"><strong>${escapeHtml(i.name)}</strong></a> <code class="muted" style="font-size:11px">${escapeHtml(i.slug)}</code></div>
        ${i.walletBaseUrl ? `<div style="margin-top:3px">${urlChip(i.walletBaseUrl)}</div>` : ''}
        ${i.error ? `<div class="error" style="font-size:11px;margin-top:2px">${escapeHtml(i.error)}</div>` : ''}
      </div>
      <div style="text-align:right;margin-left:12px">
        ${pill}
        <div style="font-size:11px;margin-top:2px;display:flex;gap:6px;justify-content:flex-end">
          ${i.status !== 'skipped' ? latencyHtml(i.latencyMs) : ''}
          ${httpBit}
        </div>
      </div>
    </div>`;
  }

  // Stop the timer if the user navigates away — observe root detachment.
  const observer = new MutationObserver(() => {
    if (!document.body.contains(root)) {
      if (timer !== undefined) window.clearInterval(timer);
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  await tick();
  // Probe live every 30s, but only while the tab is visible — admin-api
  // fans out to gateway+wallet+round-engine+webhook-dispatcher per tick,
  // so a backgrounded tab used to fire 4 GETs/10s for no reason.
  timer = window.setInterval(() => {
    if (document.visibilityState === 'visible') void tick();
  }, REFRESH_INTERVAL_MS);
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
